import { alertControls, isApiError, log, missingTranslation } from "react-better-html";
import { put } from "typed-redux-saga";
import posthog, { type CaptureOptions, type EventName, type Properties } from "posthog-js";

import { disablePostHog, passwordMinLength } from "../constants/app";

import { type ApiErrorResponse } from "../types/api";
import { type Property } from "../types/property";

import { getErrorMessage } from "./api";
import { i18n } from "./i18n";

import { userSlice } from "../redux/slices/user";

import { type ViewTransitionItem } from "../components/viewTransition/SingleViewTransition";

export function* handleError(error: unknown) {
   //* Not request error
   if (!isApiError(error)) {
      if (error instanceof Error) {
         log.error(`Something went wrong with the request.\nError: ${error.name}\nResponse: ${error.message}`);

         capturePosthogException(error);

         alertControls.createAlert({
            type: "error",
            title: i18n.t((l) => l.components.alert.error.title),
            message: getErrorMessage(),
         });
      }

      return;
   }

   //* Error data
   const errorStatusCode = error.statusCode ?? 400;
   const errorResponseData: ApiErrorResponse | "" | undefined = error.data;

   //* No user logged in
   if (errorStatusCode === 401) {
      alertControls.createAlert({
         type: "error",
         title: i18n.t((l) => l.components.alert.error.title),
         message: getErrorMessage(typeof errorResponseData === "object" ? errorResponseData.error : undefined),
      });

      yield* put(
         userSlice.actions.logout({
            doNotInformUser: true,
         }),
      );

      return;
   }

   //* Posthog error
   capturePosthogException(error, {
      errorStatusCode,
      errorResponseData,
   });

   //* No error returned from the server
   if (typeof errorResponseData === "string") {
      alertControls.createAlert({
         type: "error",
         title: i18n.t((l) => l.components.alert.error.title),
         message: getErrorMessage(),
      });
      return;
   }

   //* Inform the user with an error alert message
   if (errorStatusCode?.toString()[0] === "5") {
      alertControls.createAlert({
         type: "error",
         title: i18n.t((l) => l.components.alert.error.title),
         message: i18n.t((l) => l.api.errors.server),
      });
   } else if (errorStatusCode === 404) {
      alertControls.createAlert({
         type: "error",
         title: i18n.t((l) => l.components.alert.error.title),
         message: i18n.t((l) => l.api.errors.notFound),
      });
   } else {
      alertControls.createAlert({
         type: "error",
         title: i18n.t((l) => l.components.alert.error.title),
         message: getErrorMessage(errorResponseData?.error),
      });
   }
}

export function* handleFirebaseError(error: any) {
   const errorCode = error.code;
   const errorMessage = error.message;

   log.error(errorMessage);

   capturePosthogException(error, {
      errorCode,
      errorMessage,
   });

   alertControls.createAlert({
      type: "error",
      message: getErrorMessage(errorCode),
   });
}

export function decryptJwtToken(token: string) {
   const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");

   const jsonPayload = decodeURIComponent(
      window
         .atob(base64)
         .split("")
         .map((symbol) => "%" + ("00" + symbol.charCodeAt(0).toString(16)).slice(-2))
         .join(""),
   );

   return JSON.parse(jsonPayload);
}

export function formatPrice(number: number, currencyCode?: string) {
   const isMillion = number >= 1_000_000;
   const isBillion = number >= 1_000_000_000;

   let readyNumber = new Intl.NumberFormat("de-DE", {
      style: currencyCode ? "currency" : "decimal",
      currency: currencyCode ?? undefined,
      currencyDisplay: "symbol",
      minimumFractionDigits: isMillion || isBillion ? 0 : number % 1 === 0 ? 0 : 2,
   }).format(isBillion ? number / 1_000_000 : isMillion ? number / 1_000 : number);

   if (isMillion || isBillion) {
      const letter = isBillion ? "B" : "M";

      readyNumber = readyNumber.replaceAll(" ", `${letter} `);
      readyNumber = readyNumber.replaceAll(`0${letter}`, letter);

      if (readyNumber.includes(".00")) readyNumber = readyNumber.replaceAll(".00", "");
   } else {
      readyNumber = readyNumber.replaceAll(".", " ").replaceAll(",", ".");
   }

   return readyNumber;
}

export function capturePosthogEvent(event: EventName, properties?: Properties, options?: CaptureOptions) {
   if (disablePostHog) return;

   return posthog.capture(event, properties, options);
}

export function capturePosthogException(error: unknown, properties?: Properties) {
   if (disablePostHog) return;

   return posthog.captureException(error, properties);
}

export function emailIsValid(email: string): boolean {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

   return emailRegex.test(email);
}

export function passwordIsValid(password: string): boolean {
   const lengthPass = password.length >= passwordMinLength;
   const numberPass = /\d/.test(password);

   return lengthPass && numberPass;
}

export function urlIsValid(url: string): boolean {
   const urlRegex = /^(https?:\/\/)?([\w.-]+)\.([a-zA-Z]{2,})(\/[^\s]*)?$/;

   return urlRegex.test(url);
}

export function getViewTransitionAnimationName(viewTransitionName: ViewTransitionItem["name"]): string {
   return `${viewTransitionName}-view-transition-animation`;
}

export function getPropertyTitle(property: Property): string {
   let country = property.country
      ? i18n.t((l) => l.enums.countries[property.country as keyof typeof l.enums.countries])
      : "";
   if (country === missingTranslation) country = "";

   const elements = [property.address, property.district, property.city, country].filter(
      (element) => element && element.length,
   );

   return elements.join(", ");
}
